import { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ProjectStatus } from '@/types/project';
import { Separator } from '@/components/ui/separator';
import { ProjectFiltersErrorBoundary } from '@/components/common/ProjectFiltersErrorBoundary';
import { 
  useAccessibleProjectGroups, 
  useAccessibleProjectTypes, 
  useAccessibleStatuses, 
  useAccessibleTeamLeads,
  useProjectRoleUsers,
  useProjectRoleTypes
} from '@/hooks/useAccessibleFilterOptions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAccessibleContractYears } from '@/hooks/useAccessibleContractYears';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { PROJECT_MARKS, PROJECT_MARK_LABELS, PROJECT_MARK_BADGE_COLORS } from '@/constants/projectMarks';
import { MultiSelect } from '@/components/ui/multi-select';

export interface ProjectFiltersData {
  searchQuery: string;
  projectGroup: string;
  contractYear?: string[];
  projectType: string[];
  teamLead: string;
  status: string[];
  company: string; // 'ENP' | 'Solid' | 'Unassigned' | ''
  projectRoleUserIds?: string[]; // Filter by users who have project_roles (multi-select)
  projectMark?: string; // 'prio_1' | 'prio_2' | 'decent'
  projectRoleTypes?: string[]; // Filter by specific role types (multi-select)
  sumFilter?: boolean | null; // null = tất cả, true = SUM, false = không SUM
}

interface ProjectFiltersProps {
  filters: ProjectFiltersData;
  onFiltersChange: (filters: ProjectFiltersData) => void;
  isLoading?: boolean;
}

const ProjectFiltersContent = ({ filters, onFiltersChange, isLoading = false }: ProjectFiltersProps) => {
  // Debounced search to avoid RPC call on every keystroke
  const [localSearch, setLocalSearch] = useState(filters.searchQuery);
  const debouncedSearch = useDebouncedValue(localSearch, 300);
  const isInternalChange = useRef(false);

  // Sync debounced value → parent
  useEffect(() => {
    if (debouncedSearch !== filters.searchQuery) {
      isInternalChange.current = true;
      handleFilterChange('searchQuery', debouncedSearch);
    }
  }, [debouncedSearch]);

  // Sync parent → local (external: URL nav, clear all, browser back/forward)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setLocalSearch(filters.searchQuery);
  }, [filters.searchQuery]);

  // Feature flag for project roles
  const isProjectRolesEnabled = useGlobalFeatureFlag('ENABLE_PROJECT_ROLES');
  
  // Get accessible filter options based on user permissions
  const { data: accessibleGroups, isLoading: groupsLoading, error: groupsError } = useAccessibleProjectGroups();
  const { data: accessibleContractYears, isLoading: yearsLoading, error: yearsError } = useAccessibleContractYears();
  const { data: accessibleTypes, isLoading: typesLoading, error: typesError } = useAccessibleProjectTypes();
  const { data: accessibleStatuses, isLoading: statusesLoading, error: statusesError } = useAccessibleStatuses();
  const { data: accessibleTeamLeads, isLoading: teamLeadsLoading, error: teamLeadsError } = useAccessibleTeamLeads();
  const { data: projectRoleUsers, isLoading: roleUsersLoading, error: roleUsersError } = useProjectRoleUsers();
  const { data: projectRoleTypes, isLoading: roleTypesLoading, error: roleTypesError } = useProjectRoleTypes();

  // Show errors in filters if any
  const hasErrors = groupsError || typesError || statusesError || teamLeadsError || roleUsersError || roleTypesError || yearsError;

  const handleFilterChange = (key: keyof ProjectFiltersData, value: string | string[] | boolean | null) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilter = (key: keyof ProjectFiltersData) => {
    if (key === 'projectType' || key === 'status') {
      handleFilterChange(key, []);
    } else {
      handleFilterChange(key, '');
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      searchQuery: '',
      projectGroup: '',
      contractYear: [],
      projectType: [],
      teamLead: '',
      status: [],
      company: '',
      projectRoleUserIds: [],
      projectMark: '',
      projectRoleTypes: [],
      sumFilter: null
    });
  };

  const toggleArrayFilter = (key: 'projectType' | 'status' | 'contractYear', value: string) => {
    const currentArray = filters[key] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    handleFilterChange(key, newArray);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.projectGroup) count++;
    if ((filters.contractYear?.length ?? 0) > 0) count++;
    if (filters.projectType.length > 0) count++;
    if (filters.teamLead) count++;
    if (filters.status.length > 0) count++;
    if (filters.company) count++;
    if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) count++;
    if (filters.projectMark) count++;
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) count++;
    if (filters.sumFilter !== null && filters.sumFilter !== undefined) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-0 space-y-4">
        {hasErrors && (
          <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md">
            Có lỗi khi tải dữ liệu bộ lọc. Một số bộ lọc có thể không khả dụng.
          </div>
        )}
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Tìm kiếm dự án..."
            aria-label="Tìm kiếm dự án"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-10"
          />
          {localSearch && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 px-2 h-8"
              onClick={() => setLocalSearch('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Company Filter Switch */}
        <div>
          <div className="flex flex-wrap gap-2">
            {['ENP', 'Solid', 'Unassigned'].map((company) => (
              <Badge
                key={company}
                variant={filters.company === company ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1.5 min-h-[32px] flex items-center"
                onClick={() => handleFilterChange('company', filters.company === company ? '' : company)}
              >
                {company === 'Unassigned' ? 'Chưa phân' : company}
              </Badge>
            ))}
            
            {/* SUM Filter - Báo cáo tổng hợp */}
            <Separator orientation="vertical" className="h-6 mx-1" />
            <Badge
              variant={filters.sumFilter === true ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1.5 min-h-[32px] flex items-center ${
                filters.sumFilter === true ? 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300' : ''
              }`}
              onClick={() => handleFilterChange('sumFilter', filters.sumFilter === true ? null : true)}
            >
              SUM
              {filters.sumFilter === true && <X className="ml-1 h-3 w-3" />}
            </Badge>
            <Badge
              variant={filters.sumFilter === false ? 'default' : 'outline'}
              className={`cursor-pointer px-3 py-1.5 min-h-[32px] flex items-center ${
                filters.sumFilter === false ? 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800/50 dark:text-gray-300' : ''
              }`}
              onClick={() => handleFilterChange('sumFilter', filters.sumFilter === false ? null : false)}
            >
              Không SUM
              {filters.sumFilter === false && <X className="ml-1 h-3 w-3" />}
            </Badge>

            {/* Contract Year chips */}
            <Separator orientation="vertical" className="h-6 mx-1" />
            {(accessibleContractYears || []).map((y) => (
              <Badge
                key={y}
                variant={(filters.contractYear || []).includes(y) ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1.5 min-h-[32px] flex items-center"
                onClick={() => toggleArrayFilter('contractYear', y)}
              >
                {y}
                {(filters.contractYear || []).includes(y) && <X className="ml-1 h-3 w-3" />}
              </Badge>
            ))}
          </div>
        </div>


        {/* Project Group & Team Lead Filters - Hidden */}
        {false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Project Group Filter */}
            <div className="flex gap-1">
              <Select
                value={filters.projectGroup}
                onValueChange={(value) => handleFilterChange('projectGroup', value)}
                disabled={isLoading || groupsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nhóm..." />
                </SelectTrigger>
                <SelectContent>
                  {(accessibleGroups || []).map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filters.projectGroup && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2"
                  onClick={() => clearFilter('projectGroup')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Team Lead Filter */}
            <div className="flex gap-1">
              <Select
                value={filters.teamLead}
                onValueChange={(value) => handleFilterChange('teamLead', value)}
                disabled={isLoading || teamLeadsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Lead..." />
                </SelectTrigger>
                <SelectContent>
                  {(accessibleTeamLeads || []).map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filters.teamLead && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2"
                  onClick={() => clearFilter('teamLead')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Project Type Tags */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {filters.projectType.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {filters.projectType.length}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(accessibleTypes || []).map((type) => (
              <Badge
                key={type}
                variant={filters.projectType.includes(type) ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1.5 min-h-[32px] flex items-center"
                onClick={() => toggleArrayFilter('projectType', type)}
              >
                {type}
                {filters.projectType.includes(type) && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Badge>
            ))}
          </div>
        </div>

        {/* Status + Mark + Thành viên + Chức năng - Same Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {/* Status Tags */}
          <div>
            <div className="flex flex-wrap gap-1.5">
              {(accessibleStatuses || []).map((option) => (
                <Badge
                  key={option.value}
                  variant={filters.status.includes(option.value) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs px-3 py-1.5 min-h-[32px] flex items-center"
                  onClick={() => toggleArrayFilter('status', option.value)}
                >
                  {option.label}
                  {filters.status.includes(option.value) && (
                    <X className="ml-1 h-3 w-3" />
                  )}
                </Badge>
              ))}
            </div>
          </div>

          {/* Mark Filter - Moved next to Status */}
          <div>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_MARKS.map((mark) => (
                <Badge
                  key={mark}
                  variant={filters.projectMark === mark ? 'default' : 'outline'}
                  className={`cursor-pointer text-xs px-3 py-1.5 min-h-[32px] flex items-center ${filters.projectMark === mark ? PROJECT_MARK_BADGE_COLORS[mark] : ''}`}
                  onClick={() => handleFilterChange('projectMark', filters.projectMark === mark ? '' : mark)}
                >
                  {PROJECT_MARK_LABELS[mark]}
                  {filters.projectMark === mark && (
                    <X className="ml-1 h-3 w-3" />
                  )}
                </Badge>
              ))}
            </div>
          </div>

          {/* Project Role User Filter - MultiSelect */}
          {isProjectRolesEnabled && (
            <div>
              <MultiSelect
                options={(projectRoleUsers || []).map(u => ({ value: u.id, label: u.name }))}
                value={filters.projectRoleUserIds || []}
                onChange={(ids) => handleFilterChange('projectRoleUserIds', ids)}
                placeholder="Thành viên..."
                className="w-full"
                showSelectAll
                searchPlaceholder="Tìm thành viên..."
              />
            </div>
          )}

          {/* Project Role Type Filter - MultiSelect */}
          {isProjectRolesEnabled && (
            <div>
              <MultiSelect
                options={(projectRoleTypes || []).map(t => ({ value: t, label: t }))}
                value={filters.projectRoleTypes || []}
                onChange={(types) => handleFilterChange('projectRoleTypes', types)}
                placeholder="Chức năng..."
                className="w-full"
                showSelectAll
                searchPlaceholder="Tìm chức năng..."
              />
            </div>
          )}
        </div>

        {/* Active Filters & Clear All */}
        {activeFiltersCount > 0 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Hiển thị với {activeFiltersCount} bộ lọc</span>
              <Badge variant="secondary">
                {activeFiltersCount}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="h-8"
            >
              Xóa tất cả
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ProjectFilters = (props: ProjectFiltersProps) => {
  return (
    <ProjectFiltersErrorBoundary>
      <ProjectFiltersContent {...props} />
    </ProjectFiltersErrorBoundary>
  );
};
